// Pure, view-agnostic helpers for the "Top SPINs" page (SpinLeaderboardPage).
//
// The one idea this module encodes: the table's sortable columns SWITCH between four server-computed
// rankings, they do not re-sort one list on the client. The backend can only ship a top-N out of ~435k
// SPINs, so re-sorting the top-50-by-reads by member count would quietly present an incomplete ranking as
// if it were the top 50 by members. Everything here exists to keep that distinction visible.
import type { SpinLeaderboardResponse } from '../../../Generated/SpinLeaderboardResponse.ts'
import type { SpinLeaderboardRow } from '../../../Generated/SpinLeaderboardRow.ts'
import type { SpinTaxonomyLadder } from '../../../Generated/SpinTaxonomyLadder.ts'
import { RANK_LABELS, orderSources } from './spinMetrics.logic.js'

/** Which ranking is on screen. One per list on [SpinLeaderboardResponse]. */
export type LeaderboardMetric = 'MEMBERS' | 'SEQUENCES' | 'ORDERS' | 'READS'

export interface LeaderboardMetricUi {
  key: LeaderboardMetric
  /** Column header. */
  label: string
  /** What the column counts, for the header tooltip. */
  tip: string
  value: (row: SpinLeaderboardRow) => number
}

/**
 * Column order is deliberate: members → sequences → orders → reads reads as "how big, how varied, how
 * widespread, how loud", which is the order an admin actually asks those questions in.
 */
export const LEADERBOARD_METRICS: LeaderboardMetricUi[] = [
  {
    key: 'MEMBERS',
    label: 'Members',
    tip: 'Member OTUs currently assigned to this SPIN, counting only each order\'s latest report.',
    value: (r) => r.memberCount
  },
  {
    key: 'SEQUENCES',
    label: 'Sequences',
    tip: 'Distinct member sequences. Always at most the member count, and often far below it — a wide cluster can be many orders reporting the same sequence.',
    value: (r) => r.distinctSequenceCount
  },
  {
    key: 'ORDERS',
    label: 'Orders',
    tip: 'Distinct orders contributing a member, via their latest report only.',
    value: (r) => r.orderCount
  },
  {
    key: 'READS',
    label: 'Reads',
    tip: 'Raw reads summed across every sample of every member OTU — the abundance the cluster represents.',
    value: (r) => r.totalReads
  }
]

export const metricUi = (metric: LeaderboardMetric): LeaderboardMetricUi =>
  LEADERBOARD_METRICS.find((m) => m.key === metric) ?? LEADERBOARD_METRICS[0]!

/**
 * The server-ranked list for [metric]. Generated list fields are optional (Micronaut serde omits empty
 * collections), so this always returns an array.
 */
export const rowsFor = (r: SpinLeaderboardResponse | null, metric: LeaderboardMetric): SpinLeaderboardRow[] => {
  if (!r) return []
  switch (metric) {
    case 'MEMBERS':
      return r.byMembers ?? []
    case 'SEQUENCES':
      return r.bySequences ?? []
    case 'ORDERS':
      return r.byOrders ?? []
    case 'READS':
      return r.byReads ?? []
  }
}

/** Row caps offered. Raising it costs almost nothing — the cap bounds the list, never the aggregation. */
export const LEADERBOARD_LIMIT_OPTIONS = [25, 50, 100, 250]

export const DEFAULT_LEADERBOARD_LIMIT = 50

/**
 * How much two rankings actually overlap, as a percentage of the shorter list.
 *
 * This exists because of a real property of the data rather than as decoration: members and orders are
 * close to 1:1 within a SPIN, so those two leaderboards are nearly the same table (on the production
 * snapshot the top five are the same five SPINs in the same order). Switching a sort column and seeing
 * an unchanged table reads as a broken control unless the page says why, so the page says why — with the
 * measured number, not a hardcoded claim that could drift away from the data.
 */
export const rankingOverlap = (a: SpinLeaderboardRow[], b: SpinLeaderboardRow[]): number | null => {
  const shorter = Math.min(a.length, b.length)
  if (shorter === 0) return null
  const inB = new Set(b.map((r) => r.spinId))
  const shared = a.filter((r) => inB.has(r.spinId)).length
  return (shared / shorter) * 100
}

/**
 * The disclosure that keeps a switched column honest: this is a *different* top-N, not a re-sort, and
 * (when they nearly coincide) how much it overlaps the members ranking.
 */
export const rankingNote = (r: SpinLeaderboardResponse | null, metric: LeaderboardMetric): string | null => {
  if (!r) return null
  const rows = rowsFor(r, metric)
  if (rows.length === 0) return null
  const head = `Top ${rows.length.toLocaleString()} SPINs by ${metricUi(metric).label.toLowerCase()}, ranked server-side over the whole current set.`
  if (metric === 'MEMBERS') return head
  const overlap = rankingOverlap(rows, rowsFor(r, 'MEMBERS'))
  if (overlap == null || overlap < NEAR_DUPLICATE_OVERLAP) return head
  return `${head} ${Math.round(overlap)}% of these also rank in the top ${rows.length.toLocaleString()} by members — the two questions have nearly the same answer on this data.`
}

/** Overlap at or above which two rankings are worth calling out as near-duplicates. */
const NEAR_DUPLICATE_OVERLAP = 80

// ─── Resolved taxonomy ───────────────────────────────────────────────────────────────────────────────

/** The default blast source for the taxonomy column when the response carries it. */
export const DEFAULT_TAXONOMY_SOURCE = 'Consensus'

/**
 * The ladder as a rank-ordered array. `className` rather than `class` is the Kotlin DTO's spelling
 * (`class` being reserved), so the mapping is written out once here instead of at each call site.
 */
export const ladderValues = (ladder: SpinTaxonomyLadder): Array<string | undefined> => [
  ladder.domain,
  ladder.kingdom,
  ladder.phylum,
  ladder.className,
  ladder.order,
  ladder.family,
  ladder.genus,
  ladder.species
]

/**
 * Every blast source any row resolved to, known-first. Drives the source toggle; empty when the set has
 * no resolved taxonomy at all, which the view has to handle rather than showing an empty dropdown.
 */
export const leaderboardSources = (rows: SpinLeaderboardRow[]): string[] => {
  const seen = new Set<string>()
  rows.forEach((r) => Object.keys(r.taxonomyBySource ?? {}).forEach((s) => seen.add(s)))
  return orderSources(seen)
}

/** Pick a usable source: the requested one if present, else the first available, else null. */
export const effectiveSource = (available: string[], wanted: string): string | null =>
  available.includes(wanted) ? wanted : (available[0] ?? null)

/**
 * The visible taxonomy for one row and source: the full ladder domain→species, empty ranks dropped.
 *
 * Dropping empties is a DISPLAY choice and must not be read positionally — BOLD leaves domain blank
 * where NCBI writes 'Eukaryota', so the nth element means different ranks for different sources. That
 * is why [taxonomyTitle] carries the rank labels; anything comparing ranks should use [ladderValues].
 */
export const taxonomyPath = (ladder: SpinTaxonomyLadder | undefined): string => {
  if (!ladder) return '—'
  const parts = ladderValues(ladder).filter((v): v is string => !!v && v.trim() !== '')
  return parts.length === 0 ? '—' : parts.join(' › ')
}

/** Rank-labelled full ladder for the cell tooltip, so the compacted path can always be disambiguated. */
export const taxonomyTitle = (ladder: SpinTaxonomyLadder | undefined, source: string): string => {
  if (!ladder) return `No ${source} taxonomy resolved for this SPIN.`
  const lines = ladderValues(ladder).map(
    (v, i) => `${RANK_LABELS[i]}: ${v && v.trim() !== '' ? v : '—'}`
  )
  return `${source}\n${lines.join('\n')}`
}
